import { useLocalStorageJSON } from '@highimpact/react-hooks';
import { useCallback, useEffect, useRef, useState } from 'react';

export const CONFIG_DRAWER_WIDTH_STORAGE_KEY =
  'highimpact.dashboard.configDrawerWidth';

export const CONFIG_DRAWER_DEFAULT_WIDTH = 400;
export const CONFIG_DRAWER_MIN_WIDTH = 280;
export const CONFIG_DRAWER_MAX_WIDTH = 800;

export function clampConfigDrawerWidth(
  width: number,
  viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1024
): number {
  const maxWidth = Math.min(CONFIG_DRAWER_MAX_WIDTH, viewportWidth * 0.8);
  return Math.min(Math.max(width, CONFIG_DRAWER_MIN_WIDTH), maxWidth);
}

export function computeConfigDrawerWidthFromPointer(clientX: number): number {
  if (typeof window === 'undefined') {
    return CONFIG_DRAWER_DEFAULT_WIDTH;
  }
  return clampConfigDrawerWidth(window.innerWidth - clientX);
}

export interface UsePersistedDrawerWidthOptions {
  storageKey?: string;
}

export interface UsePersistedDrawerWidthResult {
  width: number;
  isResizing: boolean;
  handleResizePointerDown: (event: React.PointerEvent<HTMLElement>) => void;
}

export function usePersistedDrawerWidth(
  options: UsePersistedDrawerWidthOptions = {}
): UsePersistedDrawerWidthResult {
  const storageKey = options.storageKey ?? CONFIG_DRAWER_WIDTH_STORAGE_KEY;
  const [storedWidth, setStoredWidth] = useLocalStorageJSON<number>(
    storageKey,
    CONFIG_DRAWER_DEFAULT_WIDTH
  );
  const [width, setWidth] = useState(() =>
    clampConfigDrawerWidth(storedWidth ?? CONFIG_DRAWER_DEFAULT_WIDTH)
  );
  const [isResizing, setIsResizing] = useState(false);
  const widthRef = useRef(width);

  useEffect(() => {
    widthRef.current = width;
  }, [width]);

  useEffect(() => {
    setWidth(clampConfigDrawerWidth(storedWidth ?? CONFIG_DRAWER_DEFAULT_WIDTH));
  }, [storedWidth]);

  useEffect(() => {
    const handleWindowResize = () => {
      setWidth((currentWidth) => clampConfigDrawerWidth(currentWidth));
    };

    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  const handleResizePointerDown = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const handle = event.currentTarget;
      handle.setPointerCapture(event.pointerId);
      setIsResizing(true);

      const handlePointerMove = (moveEvent: PointerEvent) => {
        if (moveEvent.pointerId !== event.pointerId) {
          return;
        }
        setWidth(computeConfigDrawerWidthFromPointer(moveEvent.clientX));
      };

      const handlePointerUp = (upEvent: PointerEvent) => {
        if (upEvent.pointerId !== event.pointerId) {
          return;
        }

        handle.releasePointerCapture(event.pointerId);
        setIsResizing(false);
        setStoredWidth(widthRef.current);
        handle.removeEventListener('pointermove', handlePointerMove);
        handle.removeEventListener('pointerup', handlePointerUp);
        handle.removeEventListener('pointercancel', handlePointerUp);
      };

      handle.addEventListener('pointermove', handlePointerMove);
      handle.addEventListener('pointerup', handlePointerUp);
      handle.addEventListener('pointercancel', handlePointerUp);
    },
    [setStoredWidth]
  );

  return {
    width,
    isResizing,
    handleResizePointerDown,
  };
}
